// Helper to bucket a date as 'today', 'yesterday', or 'previous'
const getDayBucket = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "previous";

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const tomorrowStart = new Date(todayStart);
  tomorrowStart.setDate(todayStart.getDate() + 1);

  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(todayStart.getDate() - 1);

  if (date >= todayStart && date < tomorrowStart) return "today";
  if (date >= yesterdayStart && date < todayStart) return "yesterday";
  if (date < yesterdayStart) return "previous";

  // Any future dates fall back to today.
  return "today";
};
import { useMemo, useState, useEffect } from "react";
import { Button } from "../../@/components/ui/button";
import { EmptyState } from "./emptyState";
import { Link } from "@tanstack/react-router";
import { BookOpenIcon, ArrowUpRight } from "lucide-react";

// Example: You may want to pass these as props or fetch from your state/store
// pendingLogs: Array of attendance log objects
// isLoading: boolean

const DEFAULT_FILTERS = ["today", "yesterday", "previous"];

const parsePresentTotal = (value) => {
  if (!value || typeof value !== "string") return null;
  const [presentRaw, totalRaw] = value.split("/");
  const present = Number(presentRaw);
  const total = Number(totalRaw);
  if (!Number.isFinite(present) || !Number.isFinite(total)) return null;
  return { present, total };
};

const isPendingAttendanceLog = (log) => {
  if (!log) return false;
  const status = String(log.status || "").trim().toUpperCase();
  if (status === "COMPLETED") return false;

  const percent = Number(log.percent);
  if (Number.isFinite(percent) && percent >= 100) return false;

  const totals = parsePresentTotal(log.present_total);
  if (totals && totals.total > 0 && totals.present >= totals.total) return false;

  return true;
};

const PendingAttendanceLogsSection = ({
  isLoading,
  pendingLogs = [],
  onReview,
  onViewAll,
  filters = DEFAULT_FILTERS,
  showFilter = true,
}) => {
  const availableFilters = useMemo(
    () => (Array.isArray(filters) && filters.length ? filters : DEFAULT_FILTERS),
    [filters]
  );

  const [filter, setFilter] = useState(availableFilters[0]);

  useEffect(() => {
    if (!availableFilters.includes(filter)) {
      setFilter(availableFilters[0]);
    }
  }, [availableFilters, filter]);

  const filtered = useMemo(() => {
    const pendingOnly = (pendingLogs || []).filter(isPendingAttendanceLog);
    if (!availableFilters.includes(filter)) return pendingOnly;
    return pendingOnly.filter((log) => {
      const logDate = new Date(log.dateRaw || log.date);
      return getDayBucket(logDate) === filter;
    });
  }, [pendingLogs, filter, availableFilters]);

  const displayItems = filtered.slice(0, 3);

  return (
    <div className="max-md:mt-3 border border-(--neutral-300) rounded-lg bg-(--neutral-50)">
      <div className="p-4">
        <h3 className="mb-1">Pending Attendance Logs</h3>
        <p className="text-(--neutral-800)">
          Review and complete pending attendance
        </p>
      </div>
      {showFilter && (
        <div className="grid grid-cols-3 justify-start gap-2 items-start p-2 relative z-0">
          {availableFilters.map((f) => (
            <Button
              key={f}
              size=""
              variant={filter === f ? "default" : "outline"}
              className={
                (filter === f
                  ? "uppercase border-(--color-primary) text-white"
                  : "") +
                "uppercase relative z-10"
              }
              onClick={() => setFilter(f)}
              type="button"
            >
              {f === "today" ? "Today" : f === "yesterday" ? "Yesterday" : "Previous"}
            </Button>
          ))}
        </div>
      )}
      <div className="flex flex-col gap-[8px]">
        <div>
          {isLoading ? (
            <div>
              {/* Skeletons or loading state */}
              <div className="h-16 bg-(--neutral-100) rounded mb-2 animate-pulse" />
              <div className="h-16 bg-(--neutral-100) rounded mb-2 animate-pulse" />
              <div className="h-16 bg-(--neutral-100) rounded mb-2 animate-pulse" />
            </div>
          ) : displayItems.length > 0 ? (
            displayItems.map((log, idx) => (
              <div
                key={log.id ?? idx}
                className={`flex items-center justify-between p-4 border border-(--neutral-300) ${
                  idx === 0
                    ? "bg-(--neutral-100)"
                    : "bg-(--neutral-50)"
                }`}
              >
                <div>
                  <div className="font-heading text-h6 mb-1">
                    {log.sessionTitle || "Untitled Session"}
                  </div>
                  <div className="text-sm font-body text-(--neutral-800)">
                    {log.dateLabel || log.dateRaw}
                  </div>
                </div>
                <Button
                  size=""
                  variant="outline"
                  className=""
                  onClick={() => onReview?.(log)}
                >
                  Review
                </Button>
              </div>
            ))
          ) : (
            <div className="flex flex-col justify-center items-center my-[12px] h-[180px] bg-(--neutral-100) font-body">
              <EmptyState
                icon={BookOpenIcon}
                description="All attendance logs are up to date."
              />
            </div>
          )}
        </div>
        <div className="mt-1 mb-2">
          <Link to="/live-classes">
            <Button
              variant="link"
              className="text-(--color-primary) w-full font-semibold hover:bg-transparent hover:text-(--color-accent)"
              onClick={onViewAll}
            >
              View All Attendance Logs
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PendingAttendanceLogsSection;
